import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Clock } from "lucide-react";
import { Badge } from "@bcc/components/ui/badge";
import type { VariantsCountObject } from "./product-select-form-row.utils";

export interface VariantStatusBadgesTestIds {
  totalVariantsText: string;
  totalVariantsCount: string;
  noVariantsDetected: string;
  allVariantsImported: string;
  importedLive: string;
  importedPending: string;
  importedRejected: string;
}

export interface VariantStatusBadgesProps {
  variantsCountObject: VariantsCountObject;
  hasAllVariantsImported: boolean;
  testIds: VariantStatusBadgesTestIds;
}

/**
 * Variant count/status badges (total, live, pending, rejected, all-imported,
 * no-variants). Shared between the Select-step row and the Configure-step
 * collapsible header so both surfaces show identical indicators.
 */
export function VariantStatusBadges({ variantsCountObject, hasAllVariantsImported, testIds }: VariantStatusBadgesProps) {
  const { t } = useTranslation();
  const hasNoVariants = variantsCountObject.totalVariants === 0;
  const showVariantBreakdown = variantsCountObject.totalVariants > 0 && !hasAllVariantsImported;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span data-testid={testIds.totalVariantsText} className="text-sm text-muted-foreground">
        {t("components.ProductSelectModal.totalVariants")}:
      </span>
      <Badge data-testid={testIds.totalVariantsCount} variant="secondary" className="min-w-[1.5rem] justify-center">
        {variantsCountObject.totalVariants}
      </Badge>

      {hasNoVariants && (
        <Badge
          data-testid={testIds.noVariantsDetected}
          className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
        >
          <AlertTriangle className="h-3 w-3" />
          {t("components.ProductSelectModal.noVariantsDetected")}
        </Badge>
      )}

      {hasAllVariantsImported && (
        <Badge
          data-testid={testIds.allVariantsImported}
          className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
        >
          <Check className="h-3 w-3" />
          {t("components.ProductSelectModal.allVariantsImported")}
        </Badge>
      )}

      {showVariantBreakdown && (
        <>
          {variantsCountObject.localVariants > 0 && (
            <Badge
              data-testid={testIds.importedLive}
              className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
            >
              <Check className="h-3 w-3" />
              {t("components.ProductSelectModal.importedLive", { count: variantsCountObject.localVariants })}
            </Badge>
          )}
          {variantsCountObject.importedRemoteVariants > 0 && (
            <Badge
              data-testid={testIds.importedPending}
              className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800"
            >
              <Clock className="h-3 w-3" />
              {t("components.ProductSelectModal.importedPending", { count: variantsCountObject.importedRemoteVariants })}
            </Badge>
          )}
        </>
      )}

      {/* Rejected status is independent of the live/pending breakdown — show it
          whenever any variant is rejected, so rejection-only products still
          surface the tag even if the breakdown is hidden. */}
      {variantsCountObject.rejectedVariants > 0 && (
        <Badge
          data-testid={testIds.importedRejected}
          className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
        >
          <AlertTriangle className="h-3 w-3" />
          {t("components.ProductSelectModal.importedRejected", { count: variantsCountObject.rejectedVariants })}
        </Badge>
      )}
    </div>
  );
}
